import { supabase } from "@/integrations/supabase/client";

export type HeygenAvatar = {
  avatar_id: string;
  avatar_name: string;
  gender?: string;
  preview_image_url?: string;
  preview_video_url?: string;
};

export type HeygenVoice = {
  voice_id: string;
  language?: string;
  gender?: string;
  name?: string;
  preview_audio?: string;
};

export type HeygenCreateVideoResponse = {
  error: any;
  data?: { video_id: string };
};

export type HeygenVideoStatusResponse = {
  error: any;
  data?: {
    video_id: string;
    status: "pending" | "waiting" | "processing" | "completed" | "failed";
    video_url?: string;
    caption_url?: string;
    duration?: number;
    created_at?: string;
    error?: string;
  };
};

// Headers for proxy requests (API key is handled by proxy server)
const headers = {
  "Accept": "application/json",
  "Content-Type": "application/json",
};

export async function listAvatars(): Promise<HeygenAvatar[]> {
  const { data, error } = await supabase.functions.invoke("heygen-list-resources", {
    body: { type: "avatars" },
  });
  if (error) throw error;
  return data?.data?.avatars ?? [];
}

export async function listVoices(): Promise<HeygenVoice[]> {
  const { data, error } = await supabase.functions.invoke("heygen-list-resources", {
    body: { type: "voices" },
  });
  if (error) throw error;
  return data?.data?.voices ?? [];
}

export async function createVideoFromText(params: {
  text: string;
  avatar_id?: string;
  voice_id?: string;
  speed?: number;
  pitch?: number;
  width?: number;
  height?: number;
  avatar_style?: "normal" | "circle" | "rounded" | string;
}): Promise<string> {
  const {
    text,
    avatar_id,
    voice_id,
    speed,
    pitch,
    width = 1280,
    height = 720,
    avatar_style = "normal",
  } = params;

  let finalAvatarId = avatar_id;
  let finalVoiceId = voice_id;

  if (!finalAvatarId) {
    const avatars = await listAvatars();
    finalAvatarId = avatars?.[0]?.avatar_id;
  }
  if (!finalVoiceId) {
    const voices = await listVoices();
    finalVoiceId = voices?.[0]?.voice_id;
  }

  if (!finalAvatarId || !finalVoiceId) {
    throw new Error("No default avatar or voice available");
  }

  const payload: any = {
    video_inputs: [
      {
        character: { type: "avatar", avatar_id: finalAvatarId, avatar_style },
        voice: { type: "text", input_text: text, voice_id: finalVoiceId },
      },
    ],
    dimension: { width, height },
  };

  if (typeof speed === "number") payload.video_inputs[0].voice.speed = speed;
  if (typeof pitch === "number") payload.video_inputs[0].voice.pitch = pitch;

  const { data, error } = await supabase.functions.invoke("heygen-create-video", {
    body: payload,
  });
  if (error) throw error;
  const videoId = data?.data?.video_id;
  if (!videoId) throw new Error("No video_id returned");
  return videoId;
}

export async function getVideoStatus(video_id: string): Promise<HeygenVideoStatusResponse["data"]> {
  const { data, error } = await supabase.functions.invoke("heygen-get-status", {
    body: { video_id },
  });
  if (error) throw error;
  return data?.data;
}
